<script setup>
/**
 * SzAudioChip - Custom waveform audio player matching Figma design.
 * Replaces native Chatwoot AudioChip with WhatsApp-style waveform bars.
 */
import { computed, onMounted, useTemplateRef, ref, getCurrentInstance } from 'vue';
import { timeStampAppendedURL } from 'dashboard/helper/URLHelper';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';

const { attachment } = defineProps({
  attachment: { type: Object, required: true },
});

defineOptions({ inheritAttrs: false });
const emit = defineEmits(['duration-update']);

const timeStampURL = computed(() => timeStampAppendedURL(attachment.dataUrl));
const audioPlayer = useTemplateRef('audioPlayer');
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackSpeed = ref(1);
const { uid } = getCurrentInstance();

// Generate random waveform pattern (seeded by attachment URL for consistency)
const waveHeights = computed(() => {
  const seed = (attachment.dataUrl || '').length;
  const bars = 50;
  const heights = [];
  let s = seed;
  for (let i = 0; i < bars; i++) {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    const h = 12 + (s % 17); // 12-28px
    heights.push(h);
  }
  return heights;
});

const progress = computed(() => {
  if (!duration.value) return 0;
  return (currentTime.value / duration.value) * 100;
});

const playedBars = computed(() => {
  return Math.floor((progress.value / 100) * waveHeights.value.length);
});

const scrubberLeft = computed(() => {
  return `${progress.value}%`;
});

const onLoadedMetadata = () => {
  duration.value = audioPlayer.value?.duration;
  emit('duration-update', formatTime(duration.value));
};

onMounted(() => {
  duration.value = audioPlayer.value?.duration;
  audioPlayer.value.playbackRate = playbackSpeed.value;
  emit('duration-update', formatTime(duration.value));
});

useEmitter('pause_playing_audio', id => {
  if (id !== uid && isPlaying.value) {
    try { audioPlayer.value.pause(); } catch {}
    isPlaying.value = false;
  }
});

const formatTime = t => {
  if (!t || Number.isNaN(t)) return '0:00';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const onTimeUpdate = () => {
  currentTime.value = audioPlayer.value?.currentTime;
  emit('duration-update', isPlaying.value ? formatTime(currentTime.value) : formatTime(duration.value));
};

const playOrPause = () => {
  if (isPlaying.value) {
    audioPlayer.value.pause();
    isPlaying.value = false;
  } else {
    emitter.emit('pause_playing_audio', uid);
    audioPlayer.value.play();
    isPlaying.value = true;
  }
};

const onEnd = () => {
  isPlaying.value = false;
  currentTime.value = 0;
  emit('duration-update', formatTime(duration.value));
};

const changeSpeed = () => {
  const speeds = [1, 1.5, 2];
  const idx = speeds.indexOf(playbackSpeed.value);
  playbackSpeed.value = speeds[(idx + 1) % speeds.length];
  audioPlayer.value.playbackRate = playbackSpeed.value;
};

const seekFromWaveform = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const pct = x / rect.width;
  const newTime = pct * duration.value;
  audioPlayer.value.currentTime = newTime;
  currentTime.value = newTime;
};
</script>

<template>
  <audio
    ref="audioPlayer"
    class="hidden"
    playsinline
    @loadedmetadata="onLoadedMetadata"
    @timeupdate="onTimeUpdate"
    @ended="onEnd"
  >
    <source :src="timeStampURL" />
  </audio>

  <div v-bind="$attrs" class="sz-audio">
    <!-- Row 1: Speed + Play + Waveform -->
    <div class="sz-audio-row">
      <button class="sz-audio-speed" @click="changeSpeed">
        {{ playbackSpeed }}x
      </button>
      <button class="sz-audio-play" @click="playOrPause">
        <svg v-if="isPlaying" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor" />
          <rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor" />
        </svg>
        <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M21.4086 9.35258C23.5305 10.5065 23.5305 13.4935 21.4086 14.6474L8.59662 21.6145C6.53435 22.736 4 21.2763 4 18.9671L4 5.0329C4 2.72368 6.53435 1.26402 8.59661 2.38548L21.4086 9.35258Z" fill="currentColor" />
        </svg>
      </button>
      <div class="sz-audio-waveform" @click="seekFromWaveform">
        <div
          v-for="(h, i) in waveHeights"
          :key="i"
          class="sz-audio-bar"
          :class="{ played: i < playedBars }"
          :style="{ height: h + 'px' }"
        />
        <div
          class="sz-audio-scrubber"
          :class="{ visible: isPlaying }"
          :style="{ left: scrubberLeft }"
        />
      </div>
    </div>

    <!-- timer (0:24) under speed pill, aligned with play bottom -->
    <div class="sz-audio-timer-inline">{{ formatTime(currentTime || duration) }}</div>
  </div>
</template>

<style scoped>
.sz-audio {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  max-width: 320px;
  box-sizing: border-box;
  padding: 4px 0;
}
.sz-audio-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}
.sz-audio-speed {
  border: none;
  border-radius: 999px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  font-family: Inter, system-ui, sans-serif;
  cursor: pointer;
  flex-shrink: 0;
  line-height: 16px;
  white-space: nowrap;
  background: #BECCBE;
  color: #11361A;
  transition: background 0.15s;
}
.sz-audio-speed:hover { background: #A8BCA8; }

.sz-audio-play {
  width: 25px;
  height: 25px;
  min-width: 25px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #236D23;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s;
}
.sz-audio-play:hover { background: rgba(35,109,35,0.08); }

.sz-audio-waveform {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 20px;
  flex: 1;
  cursor: pointer;
  position: relative;
  min-width: 0;
}
.sz-audio-bar {

  min-width: 2px;
  border-radius: 4px;
  background: rgba(7, 85, 32, 0.24);
  flex-shrink: 0;
  transition: background 0.1s;
}
.sz-audio-bar.played {
  background: #388E3C;
}
.sz-audio-scrubber {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #FFFFFF;
  box-shadow: 0px 4px 10px rgba(15, 23, 42, 0.1);
  transform: translate(-50%, -50%) scale(0);
  pointer-events: none;
  z-index: 2;
  transition: left 0.08s linear, transform 0.15s;
}
.sz-audio-scrubber.visible {
  transform: translate(-50%, -50%) scale(1);
}


/* Dark mode */
.dark .sz-audio-speed { background: rgba(138,206,138,0.15); color: #8ACE8A; }
.dark .sz-audio-speed:hover { background: #365230; }
.dark .sz-audio-play { color: #8ACE8A; }
.dark .sz-audio-play:hover { background: rgba(102,187,106,0.08); }
.dark .sz-audio-bar {

}
.dark .sz-audio-bar.played { background: #8ACE8A; }
.dark .sz-audio-scrubber { background: #E5E7EB; }


.sz-audio-timer-inline {
  font-size: 11px;
  font-weight: 500;
  color: #0F172A;
  line-height: 1;
  margin-left: 46px;
  margin-top: -2px;
}
.dark .sz-audio-timer-inline { color: #E5E7EB; }

</style>
